import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, Rocket, X, ChevronRight } from "lucide-react";

const ITEMS = [
  {
    id: "fiscal",
    label: "Configura le impostazioni fiscali",
    description: "Anno P.IVA, codice ATECO e tipo INPS",
    path: "/fiscal",
  },
  {
    id: "ateco",
    label: "Verifica il codice ATECO",
    description: "Controlla che il coefficiente di redditività sia corretto",
    path: "/fiscal",
  },
  {
    id: "inps",
    label: "Seleziona il tipo di gestione INPS",
    description: "Gestione Separata o Artigiani/Commercianti",
    path: "/fiscal",
  },
  {
    id: "invoice",
    label: "Crea la tua prima fattura",
    description: "Registra una fattura per iniziare a tracciare i tuoi incassi",
    path: "/invoices",
  },
  {
    id: "tour",
    label: "Esplora la dashboard",
    description: "Completa il tour guidato per scoprire tutte le funzionalità",
    path: "/dashboard",
  },
];

export default function SetupChecklist({
  darkMode,
  invoices,
  appSettings,
  updateAppSettings,
}) {
  const navigate = useNavigate();

  const completedMap = useMemo(() => ({
    fiscal: appSettings.hasConfiguredFiscal === true,
    ateco: appSettings.hasConfiguredFiscal === true,
    inps: appSettings.hasConfiguredFiscal === true,
    invoice: Array.isArray(invoices) && invoices.length > 0,
    tour: appSettings.tourCompleted === true,
  }), [appSettings, invoices]);

  const completedCount = Object.values(completedMap).filter(Boolean).length;
  const totalCount = ITEMS.length;
  const allCompleted = completedCount === totalCount;
  const progressPct = Math.round((completedCount / totalCount) * 100);

  // Don't show if dismissed or all completed
  if (appSettings.checklistDismissed === true || allCompleted) return null;

  return (
    <div className={`setup-checklist rounded-2xl border p-6 ${
      darkMode
        ? "bg-neutral-900 border-neutral-800"
        : "bg-white border-neutral-200"
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Rocket className={`w-5 h-5 ${darkMode ? "text-forfy-300" : "text-forfy-600"}`} />
          <h2 className={`text-sm font-semibold ${darkMode ? "text-white" : "text-neutral-900"}`}>
            Inizia con Forfy
          </h2>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            darkMode ? "bg-neutral-800 text-neutral-400" : "bg-neutral-100 text-neutral-600"
          }`}>
            {completedCount}/{totalCount}
          </span>
        </div>
        <button
          onClick={() => updateAppSettings({ checklistDismissed: true })}
          className={`p-1 rounded-lg transition-colors ${
            darkMode ? "text-neutral-500 hover:bg-neutral-800" : "text-neutral-400 hover:bg-neutral-100"
          }`}
          aria-label="Nascondi checklist"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Progress bar */}
      <div className={`h-1.5 rounded-full overflow-hidden mb-4 ${darkMode ? "bg-neutral-800" : "bg-neutral-100"}`}>
        <div
          className="h-full rounded-full bg-forfy-500 transition-all duration-500"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      {/* Items */}
      <div className="space-y-1">
        {ITEMS.map((item) => {
          const done = completedMap[item.id];
          return (
            <button
              key={item.id}
              onClick={() => !done && navigate(item.path)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                done
                  ? darkMode
                    ? "text-neutral-600"
                    : "text-neutral-400"
                  : darkMode
                    ? "text-neutral-200 hover:bg-neutral-800"
                    : "text-neutral-800 hover:bg-neutral-50"
              }`}
              disabled={done}
            >
              {done ? (
                <CheckCircle2 className="w-5 h-5 shrink-0 text-emerald-500" />
              ) : (
                <Circle className={`w-5 h-5 shrink-0 ${darkMode ? "text-neutral-700" : "text-neutral-300"}`} />
              )}
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium ${done ? "line-through" : ""}`}>
                  {item.label}
                </div>
                <div className={`text-xs ${darkMode ? "text-neutral-600" : "text-neutral-500"}`}>
                  {item.description}
                </div>
              </div>
              {!done && (
                <ChevronRight className={`w-4 h-4 shrink-0 ${darkMode ? "text-neutral-600" : "text-neutral-400"}`} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
